import { useStyles } from '../Calendar.styles';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import {
  getEventBadgeColor,
  getEventName,
  getEventTextColor,
  getEventTooltipTitle,
  getFormattedEventDuration,
} from '../helpers';
import { CalendarEventTypeId } from '../../../pages/Calendar/Calendar.types';
import { AddProcedureButton } from './AddProcedureButton.component';

interface EventBadgeProps {
  date: Date;
  index: number;
  eventType: CalendarEventTypeId;
  eventStartDate: Date;
  eventEndDate: Date;
  assessmentSessionName?: string;
  isNextProces?: boolean;
  onAddProcedure: (eventType: CalendarEventTypeId) => void;
  onOpenProcedures: (eventType: CalendarEventTypeId) => void;
  isManagePermission: boolean;
}
export const EventBadge: React.FC<EventBadgeProps> = ({
  date,
  index,
  eventType,
  assessmentSessionName,
  eventStartDate,
  eventEndDate,
  isNextProces,
  onAddProcedure,
  onOpenProcedures,
  isManagePermission,
}) => {
  const classes = useStyles();

  const ONE_DAY_TIME = 1000 * 3600 * 24;

  const date_getTime = date.getTime();
  const date_getDay = date.getDay();
  const date_getDate = date.getDate();
  const date_getMonth = date.getMonth();
  const date_getFullYear = date.getFullYear();

  const eventStartDate_getTime = eventStartDate.getTime();
  const eventStartDate_getDay = eventStartDate.getDay();
  const eventStartDate_getDate = eventStartDate.getDate();
  const eventStartDate_getMonth = eventStartDate.getMonth();
  const eventStartDate_getFullYear = eventStartDate.getFullYear();

  const eventEndDate_getTime = eventEndDate.getTime();
  const eventEndDate_getDate = eventEndDate.getDate();
  const eventEndDate_getMonth = eventEndDate.getMonth();
  const eventEndDate_getFullYear = eventEndDate.getFullYear();

  const isOneDayProcess =
    eventStartDate_getDate === eventEndDate_getDate ||
    (isNextProces &&
      eventStartDate_getDate === eventEndDate_getDate - 1 &&
      eventStartDate_getMonth === eventEndDate_getMonth);

  const getLabelWidth = () => {
    const totalEventDays = Math.ceil((eventEndDate_getTime - eventStartDate_getTime) / ONE_DAY_TIME);
    if (
      totalEventDays === 1 ||
      (eventEndDate_getDate === date_getDate && eventEndDate_getMonth === date_getMonth) ||
      (isNextProces && eventEndDate_getDate - 1 === date_getDate && eventEndDate_getMonth === date_getMonth) ||
      (eventStartDate_getDay === 0 && date_getDay === 0)
    ) {
      return 'calc(100% - 45px)';
    } else if (
      totalEventDays === 2 ||
      (totalEventDays === 4 && eventStartDate_getDay === 0) ||
      ((eventEndDate_getDate === date_getDate + 1 || eventEndDate_getDate === date_getDate + 2) &&
        eventEndDate_getMonth === date_getMonth) ||
      (isNextProces && eventEndDate_getDate - 1 === date_getDate && eventEndDate_getMonth === date_getMonth) ||
      (eventStartDate_getDay === 6 && date_getDay === 6)
    ) {
      return 'calc(200% - 45px)';
    } else if (
      totalEventDays === 3 ||
      (eventEndDate_getDate === date_getDate + 3 && eventEndDate_getMonth === date_getMonth) ||
      (isNextProces && eventEndDate_getDate - 1 === date_getDate && eventEndDate_getMonth === date_getMonth) ||
      (eventStartDate_getDay === 5 && date_getDay === 5)
    ) {
      return 'calc(300% - 45px)';
    } else return 'calc(400% - 45px)';
  };

  const getEventLabel = () => {
    const labelWidth = getLabelWidth();
    return (
      <div
        className={classes.eventLabel}
        style={{
          color: getEventTextColor(eventType),
          minWidth: labelWidth,
          maxWidth: labelWidth,
        }}
      >
        {getEventName(eventType, assessmentSessionName)}
        {!isOneDayProcess && (
          <span className={classes.eventTime}>
            {getFormattedEventDuration(eventStartDate, eventEndDate, eventType)}
          </span>
        )}
      </div>
    );
  };

  const isEventLabel = () => {
    return (
      (date_getDate === eventStartDate_getDate &&
        date_getMonth === eventStartDate_getMonth &&
        date_getFullYear === eventStartDate_getFullYear) ||
      index % 7 === 0
    );
  };

  const isEventStartBadge = () => {
    return (
      date_getDate === eventStartDate_getDate &&
      date_getMonth === eventStartDate_getMonth &&
      date_getFullYear === eventStartDate_getFullYear &&
      !isOneDayProcess
    );
  };

  const isEventEndBadge = () => {
    return (
      (isNextProces ? date_getDate === eventEndDate_getDate - 1 : date_getDate === eventEndDate_getDate) &&
      date_getMonth === eventEndDate_getMonth &&
      date_getFullYear === eventEndDate_getFullYear &&
      !isOneDayProcess
    );
  };

  const styledTooltip = {
    '& .MuiTooltip-arrow': {
      color: '#2C2D3E',
      border: '1px solid white',
      left: '50% !important',
    },
    '& .MuiTooltip-tooltip': {
      background: '#2C2D3E',
      border: '1px solid white',
      fontSize: '0.9em',
      display: 'inline',
      bottom: '20px',
    },
  };

  return (
    <>
      {date_getTime + ONE_DAY_TIME > eventStartDate_getTime &&
        date_getTime <
          (isNextProces && !isOneDayProcess ? eventEndDate_getTime - ONE_DAY_TIME + 1 : eventEndDate_getTime) && (
          <>
            <div
              onClick={() => {
                isManagePermission && !isEventEndBadge() ? onOpenProcedures(eventType) : undefined;
              }}
              className={[
                classes.eventBadge,
                isOneDayProcess ? classes.eventOneDayBadge : '',
                isEventStartBadge() ? classes.eventStartBadge : '',
                isEventEndBadge() ? classes.eventEndBadge : '',
              ].join(' ')}
              style={{
                backgroundColor: getEventBadgeColor(eventType),
                borderRight: date_getDay !== 0 ? `solid 2px ${getEventBadgeColor(eventType)}` : 'none',
              }}
            >
              {isEventLabel() && (
                <TooltipOne
                  title={getEventTooltipTitle(eventType, eventStartDate, eventEndDate, assessmentSessionName, isOneDayProcess)}
                  placement="top"
                  sx={styledTooltip}
                >
                  {getEventLabel()}
                </TooltipOne>
              )}
            </div>
            <div className={classes.addProcedureIconContainer}>
              {(isNextProces
                ? date_getDate === eventEndDate_getDate - 1
                : date_getDate === eventEndDate_getDate && date_getMonth === eventEndDate_getMonth) &&
                isManagePermission && (
                  <AddProcedureButton onClick={() => onAddProcedure(eventType)} eventType={eventType} />
                )}
            </div>
          </>
        )}
    </>
  );
};
